import { useContext, useEffect, useState } from 'react';
import { Button, Divider, Group, Input, Modal, Select, Stack, Switch, Textarea } from '@mantine/core';
import { ChurchContext } from '@/shared';
import type { MyParishView } from '@/types/myParish';
import styles from './EditMyParishModal.module.sass';
import { BULLETIN_TYPE, CHURCH_TYPE } from '@/types';
import { BULLETIN_NUMBER_MASKS, BULLETIN_TYPE_OPTIONS, validateBulletinNumber } from '@/helpers';
import { IMaskInput } from 'react-imask';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';

type EditMyParishModalProps = {
  opened: boolean;
  myParishData: MyParishView | null;
  onClose: () => void;
  onSave: (data: MyParishView) => Promise<void> | void;
};

const MIN_ROWS = 3;
const MAX_ROWS = 6;
export const EditMyParishModal = ({ opened, myParishData, onClose, onSave }: EditMyParishModalProps) => {
  const { church } = useContext(ChurchContext);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isHiddenFromApp, setIsHiddenFromApp] = useState<boolean>(false);
  const [isDioceseControl, setIsDioceseControl] = useState<boolean>(false);
  const [isEnhanced, setIsEnhanced] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [bulletinType, setBulletinType] = useState<BULLETIN_TYPE>(BULLETIN_TYPE.NONE);
  const [bulletinNumber, setBulletinNumber] = useState<string>('');
  const [bulletinNumberError, setBulletinNumberError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  useEffect(() => {
    if (opened && myParishData && Object.keys(myParishData).length > 0) {
      setIsActive(myParishData?.isActive);
      setIsHiddenFromApp(myParishData?.isHiddenFromApp);
      setIsDioceseControl(myParishData?.isDioceseControl);
      setIsEnhanced(myParishData?.isEnhanced);
      setComment(myParishData?.comment || '');
      setBulletinType(myParishData.bulletin?.type || BULLETIN_TYPE.NONE);
      setBulletinNumber(myParishData.bulletin?.number || '');
      setBulletinNumberError(null);
    }
  }, [opened, myParishData]);

  const handleActiveChange = (checked: boolean) => {
    setIsActive(checked);
    if (checked) {
      setIsHiddenFromApp(false);
    }
  };

  const handleSave = async () => {
    if (!myParishData) return;

    const currentBulletinError = validateBulletinNumber(bulletinType, bulletinNumber);
    setBulletinNumberError(currentBulletinError);
    if (currentBulletinError) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const trimmedBulletinNumber = bulletinNumber.trim();
      const updatedData: MyParishView = {
        ...myParishData,
        isActive,
        isHiddenFromApp,
        isDioceseControl,
        isEnhanced,
        bulletin: {
          type: bulletinType,
          number: bulletinType === BULLETIN_TYPE.NONE ? undefined : trimmedBulletinNumber || undefined,
        },
        comment: comment.trim() || '',
      };

      await onSave(updatedData);
    } catch (error) {
      console.error('Failed to save myParish data:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="myParish Information"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
      size="lg"
    >
      <Stack gap="md" pt="sm">
        <Group gap="xl" align="center">
          <Switch
            label="MyParish Active"
            labelPosition="left"
            checked={isActive}
            onChange={(e) => handleActiveChange(e.currentTarget.checked)}
          />
          <Switch
            label="Do not display in App"
            labelPosition="left"
            checked={isHiddenFromApp}
            disabled={isActive}
            onChange={(e) => setIsHiddenFromApp(e.currentTarget.checked)}
          />
        </Group>

        {church?.type === CHURCH_TYPE.DIOCESE && (
          <Group gap="xs">
            <Switch
              label="Diocese Control"
              labelPosition="left"
              checked={isDioceseControl}
              onChange={(e) => setIsDioceseControl(e.currentTarget.checked)}
            />
          </Group>
        )}

        <Stack gap="xs">
          <Select
            label="Bulletin Type"
            data={BULLETIN_TYPE_OPTIONS}
            value={bulletinType}
            onChange={(value) => {
              const nextType = (value as BULLETIN_TYPE) || BULLETIN_TYPE.NONE;
              setBulletinType(nextType);
              const error = validateBulletinNumber(nextType, bulletinNumber);
              setBulletinNumberError(error);
            }}
          />
          {bulletinType !== BULLETIN_TYPE.NONE && (
            <Input.Wrapper label="Bulletin Number" error={bulletinNumberError}>
              <Input
                value={bulletinNumber}
                component={IMaskInput}
                mask={BULLETIN_NUMBER_MASKS[bulletinType]}
                onChange={(e) => {
                  setBulletinNumber(e.currentTarget.value);
                  setBulletinNumberError(validateBulletinNumber(bulletinType, e.currentTarget.value));
                }}
                placeholder={
                  bulletinType === BULLETIN_TYPE.DIOCESAN
                    ? '####'
                    : bulletinType === BULLETIN_TYPE.LPI
                      ? '##-####'
                      : '######'
                }
              ></Input>
            </Input.Wrapper>
          )}
        </Stack>

        <Stack gap="xs">
          <Switch
            label="Enhanced"
            labelPosition="left"
            checked={isEnhanced}
            onChange={(e) => setIsEnhanced(e.currentTarget.checked)}
          />
        </Stack>

        {isActive && (
          <>
            <Stack gap="xs">
              <Textarea
                label="Comment (Internal Use Only)"
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
                placeholder="Enter internal comments..."
                minRows={MIN_ROWS}
                maxRows={MAX_ROWS}
              />
            </Stack>
          </>
        )}
        <Divider my="xs" />
        <SaveErrorBox error={saveError} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving} color="primary">
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
